import React from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import { api, TOKEN_KEY, formatApiError } from "@/lib/api";
import {
    LayoutDashboard,
    CalendarCheck,
    Inbox,
    LogOut,
    MoreVertical,
    RefreshCw,
    CheckCircle2,
    XCircle,
    Clock3,
    Award,
    Phone,
    Mail,
    Trash2,
} from "lucide-react";

const STATUS_STYLES = {
    pending: "bg-[#d4af37]/15 text-[#8a6a00] border-[#d4af37]/40",
    confirmed: "bg-[#114b5f]/10 text-[#114b5f] border-[#114b5f]/30",
    completed: "bg-[#2d6a4f]/10 text-[#2d6a4f] border-[#2d6a4f]/30",
    cancelled: "bg-[#9b2226]/10 text-[#9b2226] border-[#9b2226]/30",
};

const STATUS_ICON = {
    pending: Clock3,
    confirmed: CalendarCheck,
    completed: Award,
    cancelled: XCircle,
};

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const StatCard = ({ label, value, tone }) => (
    <div
        data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className="rounded-2xl border border-[#e8e4db] bg-white p-6"
    >
        <div className="text-xs uppercase tracking-[0.25em] text-[#7a8582]">
            {label}
        </div>
        <div
            className={`mt-3 font-serif-display text-4xl ${
                tone || "text-[#114b5f]"
            }`}
        >
            {value}
        </div>
    </div>
);

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [user, setUser] = React.useState(null);
    const [stats, setStats] = React.useState(null);
    const [appointments, setAppointments] = React.useState([]);
    const [messages, setMessages] = React.useState([]);
    const [statusFilter, setStatusFilter] = React.useState("all");
    const [loading, setLoading] = React.useState(true);
    const [activeTab, setActiveTab] = React.useState("appointments");

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const [me, s, apps, msgs] = await Promise.all([
                api.get("/auth/me"),
                api.get("/admin/stats"),
                api.get("/admin/appointments", {
                    params:
                        statusFilter === "all"
                            ? {}
                            : { status_filter: statusFilter },
                }),
                api.get("/admin/contact-messages"),
            ]);
            setUser(me.data);
            setStats(s.data);
            setAppointments(apps.data);
            setMessages(msgs.data);
        } catch (err) {
            if (err?.response?.status === 401) {
                window.localStorage.removeItem(TOKEN_KEY);
                navigate("/admin/login", { replace: true });
                return;
            }
            toast.error(
                formatApiError(err?.response?.data?.detail) ||
                    "Could not load data.",
            );
        } finally {
            setLoading(false);
        }
    }, [statusFilter, navigate]);

    React.useEffect(() => {
        if (!window.localStorage.getItem(TOKEN_KEY)) {
            navigate("/admin/login", { replace: true });
            return;
        }
        load();
    }, [load, navigate]);

    const logout = () => {
        window.localStorage.removeItem(TOKEN_KEY);
        toast.success("Signed out");
        navigate("/admin/login", { replace: true });
    };

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/appointments/${id}`, { status });
            toast.success(`Marked as ${status}`);
            load();
        } catch (err) {
            toast.error(
                formatApiError(err?.response?.data?.detail) ||
                    "Update failed.",
            );
        }
    };

    const removeAppt = async (id) => {
        if (!window.confirm("Delete this appointment?")) return;
        try {
            await api.delete(`/admin/appointments/${id}`);
            toast.success("Appointment deleted");
            load();
        } catch (err) {
            toast.error(
                formatApiError(err?.response?.data?.detail) ||
                    "Delete failed.",
            );
        }
    };

    return (
        <div className="min-h-screen bg-[#faf9f6] flex">
            {/* Sidebar */}
            <aside className="hidden md:flex w-64 bg-[#0d3a4a] text-[#faf9f6] flex-col p-6 sticky top-0 h-screen">
                <Link
                    to="/"
                    className="flex items-baseline gap-2 mb-10"
                    data-testid="admin-logo"
                >
                    <span className="font-serif-display text-3xl">
                        Galaxy
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">
                        Admin
                    </span>
                </Link>
                <nav className="space-y-1 text-sm">
                    <button
                        type="button"
                        onClick={() => setActiveTab("appointments")}
                        data-testid="sidebar-appointments"
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition ${
                            activeTab === "appointments"
                                ? "bg-white/10 text-[#faf9f6]"
                                : "text-[#faf9f6]/70 hover:text-[#faf9f6]"
                        }`}
                    >
                        <LayoutDashboard size={16} strokeWidth={1.5} />
                        Appointments
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab("messages")}
                        data-testid="sidebar-messages"
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition ${
                            activeTab === "messages"
                                ? "bg-white/10 text-[#faf9f6]"
                                : "text-[#faf9f6]/70 hover:text-[#faf9f6]"
                        }`}
                    >
                        <Inbox size={16} strokeWidth={1.5} />
                        Contact Messages
                    </button>
                </nav>
                <div className="mt-auto pt-6 border-t border-white/10">
                    <div className="text-xs text-[#faf9f6]/60">
                        Signed in as
                    </div>
                    <div className="text-sm mt-1">{user?.email || "…"}</div>
                    <button
                        type="button"
                        onClick={logout}
                        data-testid="admin-logout-button"
                        className="mt-4 inline-flex items-center gap-2 text-xs text-[#d4af37] hover:text-white"
                    >
                        <LogOut size={14} strokeWidth={1.5} />
                        Sign out
                    </button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 min-w-0">
                <div className="mx-auto max-w-6xl px-6 md:px-10 py-8">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <div className="text-xs uppercase tracking-[0.3em] text-[#7a8582]">
                                Dashboard
                            </div>
                            <h1 className="font-serif-display text-3xl md:text-4xl text-[#114b5f]">
                                Appointments & Enquiries
                            </h1>
                        </div>
                        <button
                            type="button"
                            onClick={load}
                            data-testid="admin-refresh-button"
                            className="inline-flex items-center gap-2 rounded-full border border-[#e8e4db] bg-white px-4 py-2 text-xs uppercase tracking-wide text-[#4a5250] hover:border-[#114b5f]"
                        >
                            <RefreshCw
                                size={14}
                                className={loading ? "animate-spin" : ""}
                            />
                            Refresh
                        </button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <StatCard
                            label="Total"
                            value={stats?.total_appointments ?? "—"}
                        />
                        <StatCard
                            label="Pending"
                            value={stats?.pending ?? "—"}
                            tone="text-[#8a6a00]"
                        />
                        <StatCard
                            label="Confirmed"
                            value={stats?.confirmed ?? "—"}
                        />
                        <StatCard
                            label="Messages"
                            value={stats?.contact_messages ?? "—"}
                            tone="text-[#2d6a4f]"
                        />
                    </div>

                    <Tabs
                        value={activeTab}
                        onValueChange={setActiveTab}
                        className="w-full"
                    >
                        <TabsList
                            data-testid="admin-tabs"
                            className="bg-[#f3f0ea] rounded-full p-1 mb-6"
                        >
                            <TabsTrigger
                                value="appointments"
                                data-testid="tab-appointments"
                                className="rounded-full data-[state=active]:bg-[#114b5f] data-[state=active]:text-[#faf9f6] px-5"
                            >
                                Appointments
                            </TabsTrigger>
                            <TabsTrigger
                                value="messages"
                                data-testid="tab-messages"
                                className="rounded-full data-[state=active]:bg-[#114b5f] data-[state=active]:text-[#faf9f6] px-5"
                            >
                                Contact messages
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="appointments">
                            <div className="flex items-center gap-2 mb-4">
                                {[
                                    "all",
                                    "pending",
                                    "confirmed",
                                    "completed",
                                    "cancelled",
                                ].map((s) => (
                                    <button
                                        type="button"
                                        key={s}
                                        onClick={() => setStatusFilter(s)}
                                        data-testid={`filter-${s}`}
                                        className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border transition ${
                                            statusFilter === s
                                                ? "bg-[#114b5f] text-[#faf9f6] border-[#114b5f]"
                                                : "bg-white text-[#4a5250] border-[#e8e4db] hover:border-[#114b5f]"
                                        }`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>

                            <div
                                data-testid="admin-booking-table"
                                className="rounded-3xl border border-[#e8e4db] bg-white overflow-hidden"
                            >
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-[#f3f0ea] hover:bg-[#f3f0ea]">
                                            <TableHead>Patient</TableHead>
                                            <TableHead>Service</TableHead>
                                            <TableHead>Slot</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Booked on</TableHead>
                                            <TableHead className="text-right">
                                                Actions
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {appointments.length === 0 && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={6}
                                                    className="py-14 text-center text-[#7a8582]"
                                                >
                                                    {loading
                                                        ? "Loading…"
                                                        : "No appointments yet."}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {appointments.map((a) => {
                                            const Icon =
                                                STATUS_ICON[a.status] ||
                                                Clock3;
                                            return (
                                                <TableRow
                                                    key={a.id}
                                                    data-testid={`booking-row-${a.id}`}
                                                >
                                                    <TableCell>
                                                        <div className="font-medium text-[#1a1d1c]">
                                                            {a.name}
                                                        </div>
                                                        <div className="text-xs text-[#7a8582] flex items-center gap-2 mt-0.5">
                                                            <Phone
                                                                size={11}
                                                                strokeWidth={
                                                                    1.5
                                                                }
                                                            />{" "}
                                                            {a.phone}
                                                        </div>
                                                        {a.email && (
                                                            <div className="text-xs text-[#7a8582] flex items-center gap-2 mt-0.5">
                                                                <Mail
                                                                    size={
                                                                        11
                                                                    }
                                                                    strokeWidth={
                                                                        1.5
                                                                    }
                                                                />{" "}
                                                                {a.email}
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {a.service}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div>
                                                            {a.preferred_date}
                                                        </div>
                                                        <div className="text-xs text-[#7a8582]">
                                                            {a.preferred_time}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge
                                                            variant="outline"
                                                            className={`gap-1 rounded-full font-normal ${
                                                                STATUS_STYLES[
                                                                    a.status
                                                                ]
                                                            }`}
                                                        >
                                                            <Icon
                                                                size={12}
                                                                strokeWidth={
                                                                    1.5
                                                                }
                                                            />
                                                            {a.status}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-xs text-[#7a8582]">
                                                        {formatDate(
                                                            a.created_at,
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger
                                                                asChild
                                                            >
                                                                <button
                                                                    type="button"
                                                                    data-testid={`actions-${a.id}`}
                                                                    className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-[#f3f0ea]"
                                                                >
                                                                    <MoreVertical
                                                                        size={
                                                                            16
                                                                        }
                                                                        strokeWidth={
                                                                            1.5
                                                                        }
                                                                    />
                                                                </button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent
                                                                align="end"
                                                                className="bg-white"
                                                            >
                                                                <DropdownMenuItem
                                                                    onClick={() =>
                                                                        updateStatus(
                                                                            a.id,
                                                                            "confirmed",
                                                                        )
                                                                    }
                                                                >
                                                                    <CheckCircle2
                                                                        size={
                                                                            14
                                                                        }
                                                                        className="mr-2"
                                                                    />
                                                                    Mark confirmed
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem
                                                                    onClick={() =>
                                                                        updateStatus(
                                                                            a.id,
                                                                            "completed",
                                                                        )
                                                                    }
                                                                >
                                                                    <Award
                                                                        size={
                                                                            14
                                                                        }
                                                                        className="mr-2"
                                                                    />
                                                                    Mark completed
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem
                                                                    onClick={() =>
                                                                        updateStatus(
                                                                            a.id,
                                                                            "pending",
                                                                        )
                                                                    }
                                                                >
                                                                    <Clock3
                                                                        size={
                                                                            14
                                                                        }
                                                                        className="mr-2"
                                                                    />
                                                                    Mark pending
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem
                                                                    onClick={() =>
                                                                        updateStatus(
                                                                            a.id,
                                                                            "cancelled",
                                                                        )
                                                                    }
                                                                >
                                                                    <XCircle
                                                                        size={
                                                                            14
                                                                        }
                                                                        className="mr-2"
                                                                    />
                                                                    Cancel
                                                                </DropdownMenuItem>
                                                                <DropdownMenuSeparator />
                                                                <DropdownMenuItem
                                                                    onClick={() =>
                                                                        removeAppt(
                                                                            a.id,
                                                                        )
                                                                    }
                                                                    className="text-[#9b2226] focus:text-[#9b2226]"
                                                                >
                                                                    <Trash2
                                                                        size={
                                                                            14
                                                                        }
                                                                        className="mr-2"
                                                                    />
                                                                    Delete
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        </TabsContent>

                        <TabsContent value="messages">
                            <div className="rounded-3xl border border-[#e8e4db] bg-white overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-[#f3f0ea] hover:bg-[#f3f0ea]">
                                            <TableHead>From</TableHead>
                                            <TableHead>Subject</TableHead>
                                            <TableHead>Message</TableHead>
                                            <TableHead>Received</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {messages.length === 0 && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={4}
                                                    className="py-14 text-center text-[#7a8582]"
                                                >
                                                    {loading
                                                        ? "Loading…"
                                                        : "No messages yet."}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {messages.map((m) => (
                                            <TableRow
                                                key={m.id}
                                                data-testid={`message-row-${m.id}`}
                                            >
                                                <TableCell>
                                                    <div className="font-medium">
                                                        {m.name}
                                                    </div>
                                                    <div className="text-xs text-[#7a8582]">
                                                        {m.email}
                                                    </div>
                                                    {m.phone && (
                                                        <div className="text-xs text-[#7a8582]">
                                                            {m.phone}
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {m.subject || "—"}
                                                </TableCell>
                                                <TableCell className="max-w-md whitespace-normal text-sm text-[#4a5250]">
                                                    {m.message}
                                                </TableCell>
                                                <TableCell className="text-xs text-[#7a8582]">
                                                    {formatDate(m.created_at)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>
            </main>
        </div>
    );
}
